import { createBrowserRouter } from "react-router-dom";
import React, { Suspense, lazy } from "react";
import Main from "../Layout/Main";
import Home from "../Pages/Home/Home";
import ScrollToTop from "../Components/ScrollToTop";

// Lazy load all non-critical pages for code splitting
const Contact = lazy(() => import("../Pages/Contact/Contact"));
const SingleListing = lazy(
  () => import("../Pages/Single Listing/SingleListing"),
);
const Properties = lazy(() => import("../Pages/Properties/Properties"));
const About = lazy(() => import("../Pages/About/About"));
const Dashboard = lazy(() => import("../Dashboard/Dashboard"));
const Reservations = lazy(() => import("../Dashboard/Pages/Reservations"));
const Services = lazy(() => import("../Pages/Services/Services"));
const ListWithUs = lazy(() => import("../Pages/ListWithUs/ListWithUs"));
const BlogPage = lazy(() => import("../Pages/Blog/BlogPage"));
const PrivacyPolicy = lazy(
  () => import("../Pages/Privacy Policy/PrivacyPolicy"),
);
const BlogDetails = lazy(() => import("../Pages/Blog/BlogDetails"));
const PropertiesOnMap = lazy(
  () => import("../Pages/PropertiesOnMap/PropertiesOnMap"),
);
const TermsAndCondition = lazy(
  () => import("../Pages/TermsAndCondition/TermsAndCondition"),
);
const ThankYou = lazy(() => import("../Pages/ThankYou/ThankYou"));
const SitemapXml = lazy(() => import("../utilities/SitemapXml"));
const PaymentSuccess = lazy(() => import("../Pages/Payment/PaymentSuccess"));
const PaymentFailed = lazy(() => import("../Pages/Payment/PaymentFailed"));
const PaymentCancelled = lazy(
  () => import("../Pages/Payment/PaymentCancelled"),
);
const Error = lazy(() => import("../Pages/Error"));
const Businesses = lazy(() => import("../Pages/Businesses/Businesses"));

// Lazy load Stripe - only when checkout is accessed
const StripeCheckoutWrapper = lazy(() => import("./StripeCheckoutWrapper"));

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-gray-500">Loading...</div>
  </div>
);

// Wrap lazy components with Suspense
const withSuspense = (Component) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);
export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <ScrollToTop /> <Main />
      </>
    ),
    errorElement: withSuspense(Error),
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/contact",
        element: withSuspense(Contact),
      },
      {
        path: "/about",
        element: withSuspense(About),
      },
      {
        path: "/experiences",
        element: withSuspense(Services),
      },
      {
        path: "/list-with-us",
        element: withSuspense(ListWithUs),
      },
      {
        path: "/blog",
        element: withSuspense(BlogPage),
      },
      {
        path: "/brands",
        element: withSuspense(Businesses),
      },
      {
        path: "/blog/:slug",
        element: withSuspense(BlogDetails),
      },
      {
        path: "/privacy-policy",
        element: withSuspense(PrivacyPolicy),
      },
      {
        path: "/terms-and-conditions",
        element: withSuspense(TermsAndCondition),
      },
      {
        path: "/thank-you",
        element: withSuspense(ThankYou),
      },
      {
        path: "/payment-success",
        element: withSuspense(PaymentSuccess),
      },
      {
        path: "/payment-cancelled",
        element: withSuspense(PaymentCancelled),
      },
      {
        path: "/payment-failed",
        element: withSuspense(PaymentFailed),
      },
      {
        path: "/short-term-rentals",
        element: withSuspense(PropertiesOnMap),
      },
      {
        path: "/short-term-rentals/:location",
        element: withSuspense(PropertiesOnMap),
      },
      {
        path: "/sitemap.xml",
        element: withSuspense(SitemapXml),
      },
      {
        path: "/properties",
        element: (
          <>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Properties />
            </Suspense>
          </>
        ),
      },
      {
        path: "/checkout",
        element: withSuspense(StripeCheckoutWrapper),
      },
      {
        path: "listing/:slug",
        element: withSuspense(SingleListing),
      },
    ],
  },
  {
    path: "/dashboard",
    element: withSuspense(Dashboard),
    children: [
      {
        path: "/dashboard",
        element: withSuspense(Reservations),
      },
    ],
  },
]);
